import { useState, useCallback } from 'react';
import { TeamUser, EditUserDto, UserRole } from '../types/team.types';

// Mock data for team members
const MOCK_TEAM_USERS: TeamUser[] = [
  {
    id: '1',
    username: 'john_admin',
    first_name: 'John',
    last_name: 'Smith',
    email: 'john.smith@drivitive.com',
    role: 'admin',
    active: true,
    last_login: '2024-01-15T10:30:00Z'
  },
  {
    id: '2',
    username: 'sarah_seo',
    first_name: 'Sarah',
    last_name: 'Johnson',
    email: 'sarah.johnson@drivitive.com',
    role: 'seo',
    active: true,
    last_login: '2024-01-14T16:45:00Z'
  },
  {
    id: '3',
    username: 'mike_admin',
    first_name: 'Mike',
    last_name: 'Brown',
    email: 'mike.brown@drivitive.com',
    role: 'admin',
    active: true,
    last_login: '2024-01-13T09:15:00Z'
  },
  {
    id: '4',
    username: 'emma_seo',
    first_name: 'Emma',
    last_name: 'Davis',
    email: 'emma.davis@drivitive.com',
    role: 'seo',
    active: false,
    last_login: '2024-01-10T14:20:00Z'
  }
];

export const useTeam = () => {
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>(MOCK_TEAM_USERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateUser = useCallback(async (userId: string, data: EditUserDto): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      setTeamUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, ...data }
            : user
        )
      );
    } catch (err) {
      setError('Failed to update user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUser = useCallback((userId: string): TeamUser | undefined => {
    return teamUsers.find(user => user.id === userId);
  }, [teamUsers]);

  return {
    teamUsers,
    loading,
    error,
    updateUser,
    getUser
  };
};

export const useTeamUser = (userId: string) => {
  const { getUser, updateUser, loading, error } = useTeam();
  const user = getUser(userId);

  return {
    user,
    updateUser: (data: EditUserDto) => updateUser(userId, data),
    loading,
    error
  };
};